import type { ReactNode } from "react";

import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

import { initials } from "./styles";

/**
 * The top of a record page (W12 U2): an initials avatar (round for people,
 * square for companies and deals), the name, chips (types, stage, signals),
 * a line of key facts, and the page's main actions on the right.
 */
export function RecordHeader({
  name,
  shape = "round",
  chips,
  facts,
  actions,
  children,
}: {
  name: string;
  shape?: "round" | "square";
  chips?: ReactNode;
  /** Key facts, already separated; rendered small and muted. */
  facts?: ReactNode;
  actions?: ReactNode;
  /** Anything below the header line (a stage path, a merge notice). */
  children?: ReactNode;
}) {
  return (
    <Card className="gap-4 px-4 py-4 sm:px-6 sm:py-5">
      <div className="flex flex-wrap items-start gap-4">
        <div
          aria-hidden
          className={cn(
            "flex size-10 shrink-0 items-center justify-center bg-primary text-sm font-semibold text-primary-foreground sm:size-12 sm:text-base",
            shape === "round" ? "rounded-full" : "rounded-lg",
          )}
        >
          {initials(name)}
        </div>
        <div className="min-w-[12rem] flex-1 space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">
              {name}
            </h1>
            {chips}
          </div>
          {facts ? (
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
              {facts}
            </div>
          ) : null}
        </div>
        {actions ? (
          <div className="flex flex-wrap items-center gap-2">{actions}</div>
        ) : null}
      </div>
      {children}
    </Card>
  );
}

/** A dot between header facts. */
export const FactDot = () => <span aria-hidden>·</span>;
